import { useEffect, useRef, useState } from "preact/hooks";
import { X } from "lucide-react";

export interface FillsUpsellPopupProps {
  quantity: number;
  roseMistPriceCents: number;
  fourFillsPriceCents: number;
  onAccept: () => Promise<void>;
  onKeepMist: () => void;
  onClose: () => void;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function FillsUpsellPopup({
  quantity,
  roseMistPriceCents,
  fourFillsPriceCents,
  onAccept,
  onKeepMist,
  onClose,
}: FillsUpsellPopupProps) {
  const [submitting, setSubmitting] = useState(false);
  const primaryButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, submitting]);

  // Intercepting the theme's add-to-cart click leaves browser focus on the
  // (now-hidden) ADD TO CART button. Without moving focus into the dialog,
  // a stray Enter keypress re-submits that button and double-adds.
  useEffect(() => {
    primaryButtonRef.current?.focus();
  }, []);

  const accept = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onAccept();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="tw:fixed tw:inset-0 tw:z-9999 tw:flex tw:items-end tw:justify-center tw:sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Switch to 4 Fills"
    >
      <div
        className="tw:absolute tw:inset-0 tw:bg-black/50"
        onClick={() => {
          if (!submitting) onClose();
        }}
      />
      <div className="tw:relative tw:w-full tw:max-w-md tw:rounded-t-2xl tw:bg-white tw:p-6 tw:shadow-xl tw:sm:rounded-2xl">
        <button
          type="button"
          aria-label="Close"
          className="tw:absolute tw:right-4 tw:top-4 tw:text-gray-400 tw:hover:text-gray-600"
          onClick={() => {
            if (!submitting) onClose();
          }}
          disabled={submitting}
        >
          <X size={20} />
        </button>
        <h2 className="tw:text-lg tw:font-semibold tw:text-gray-900">
          Refill &amp; save
        </h2>
        <p className="tw:mt-2 tw:text-sm tw:text-gray-600">
          You&apos;ve bought Rose Mist before — switch to 4 Fills (8oz) and
          refill the bottle you already have.
        </p>
        <div className="tw:mt-4 tw:flex tw:items-center tw:justify-between tw:rounded-lg tw:border tw:border-gray-200 tw:p-3 tw:text-sm">
          <span className="tw:text-gray-500">Rose Mist 2oz</span>
          <span className="tw:text-gray-500">
            {formatCents(roseMistPriceCents * quantity)}
          </span>
        </div>
        <div className="tw:mt-2 tw:flex tw:items-center tw:justify-between tw:rounded-lg tw:border-2 tw:border-pink-300 tw:bg-pink-50 tw:p-3 tw:text-sm">
          <span className="tw:font-medium tw:text-gray-900">4 Fills 8oz</span>
          <span className="tw:font-medium tw:text-gray-900">
            {formatCents(fourFillsPriceCents * quantity)}
          </span>
        </div>
        <button
          ref={primaryButtonRef}
          type="button"
          className="tw:mt-5 tw:w-full tw:rounded-full tw:bg-gray-900 tw:px-4 tw:py-3 tw:text-sm tw:font-semibold tw:text-white tw:hover:bg-gray-700 tw:disabled:opacity-60"
          onClick={accept}
          disabled={submitting}
        >
          {submitting ? "Adding…" : "Switch to 4 Fills"}
        </button>
        <button
          type="button"
          className="tw:mt-2 tw:w-full tw:rounded-full tw:border tw:border-gray-300 tw:px-4 tw:py-3 tw:text-sm tw:font-medium tw:text-gray-700 tw:hover:bg-gray-50"
          onClick={onKeepMist}
          disabled={submitting}
        >
          Keep Rose Mist 2oz
        </button>
      </div>
    </div>
  );
}
